/* eslint-disable */
import React, { useState, useContext } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { Formik, Form } from 'formik';
import showServerError from 'helpers/showError';
import { ALL_ROUTES } from 'config/routes';
import { useMutation } from 'react-fetching-library';
import FormikTextField from 'components/FormikFields/FormikTextField';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import { AppRouteContext } from 'contexts';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../redux_store/reducer/reducers/notificationsReducer';

const useStyles = makeStyles(theme => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'auto',
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
    marginBottom: theme.spacing(10),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    width: '100%',
  },
  formContent: {
    width: '100%',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  passwordSection: {
    maxWidth: 500,
    margin: '0 auto',
    textAlign: 'center',
  },
  sectionHeader: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: 8,
    padding: '20px 28px',
    margin: theme.spacing(3, 'auto'),
    width: 'fit-content',
    maxWidth: '100%',
  },
  warningIcon: {
    color: '#856404',
    fontSize: 20,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  warningText: {
    color: '#856404',
    fontSize: 14,
    lineHeight: 1.5,
    '& strong': {
      display: 'block',
      textAlign: 'center',
      marginBottom: theme.spacing(1.5),
    },
    '& ul': {
      margin: 0,
      paddingLeft: theme.spacing(2),
      textAlign: 'left',
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
  },
  passwordFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  passwordHintBox: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: 8,
    padding: '20px 28px',
    margin: theme.spacing(2, 'auto', 1.5, 'auto'),
    width: 'fit-content',
    maxWidth: '100%',
    opacity: 0,
    animation: '$fadeIn 0.4s ease-in-out forwards',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  hintIcon: {
    color: '#1976d2',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  hintText: {
    color: '#1976d2',
    fontSize: 13,
    lineHeight: 1.5,
    '& strong': {
      display: 'block',
      textAlign: 'center',
      marginBottom: theme.spacing(1.5),
    },
    '& > div:first-of-type': {
      textAlign: 'center',
      marginBottom: theme.spacing(1),
    },
    '& ul': {
      margin: 0,
      paddingLeft: theme.spacing(2),
      textAlign: 'left',
    },
    '& li': {
      marginBottom: theme.spacing(0.25),
    },
    '& .continue-text': {
      fontStyle: 'italic',
      marginTop: theme.spacing(1.5),
      textAlign: 'center',
    },
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const PasswordChange = () => {
  const classes = useStyles();
  const {
    en: { errors, notifications, forms, btn, titles },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const [backendErrors, setBackendErrors] = useState({});
  const { setAppRoute } = useContext(AppRouteContext);
  const { user } = useSelector(state => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [hasBlurredPassword, setHasBlurredPassword] = useState(false);

  const passwordChangeAction = values => ({
    method: 'POST',
    endpoint: '/user/password',
    body: values,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [passwordChangeAction]);

  const changePassword = async (values, { setSubmitting }) => {
    setBackendErrors({});
    const { payload, error, status } = await mutate(values);
    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      setSubmitting(false);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };

      switch (status) {
        case 422: {
          showServerError(dispatch, errors.validationError);
          setBackendErrors(payload.validationErrors);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
      dispatch(
        enqueueSnackbar({
          message: notifications.passwordHasBeenChanged,
          options: { variant: SNACKBAR_VARIANTS.success },
        })
      );
      setAppRoute({
        path: ALL_ROUTES.root.path,
      });
    }
  };

  const checkPasswordStrength = password => {
    const issues = [];

    if (password.length < 8) {
      issues.push('At least 8 characters');
    }

    if (!/[a-z]/.test(password)) {
      issues.push('Lowercase letters');
    }

    if (!/[A-Z]/.test(password)) {
      issues.push('Uppercase letters');
    }

    if (!/\d/.test(password)) {
      issues.push('Numbers');
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      issues.push('Special characters');
    }

    return issues;
  };

  const validateForm = values => {
    const errors = {};

    if (!values.confirmPassword) {
      errors.confirmPassword = 'New password is required';
    }

    if (!values.password) {
      errors.password = 'Please confirm your new password';
    }

    if (values.confirmPassword && values.password && values.confirmPassword !== values.password) {
      errors.password = 'Passwords do not match';
    }

    return errors;
  };

  return (
    <Formik
      initialValues={{
        newPassword: '',
        password: '',
      }}
      validate={validateForm}
      validateOnBlur={false}
      onSubmit={changePassword}
    >
      {({ isValid, isSubmitting, dirty, handleSubmit, values, errors }) => {
        const passwordIssues = values.confirmPassword
          ? checkPasswordStrength(values.confirmPassword)
          : [];
        const showPasswordHint =
          hasBlurredPassword && values.confirmPassword && passwordIssues.length > 0;

        return (
          <ContentWrapper
            titleText={titles.changePassword}
            containerStyle={{
              overflow: 'visible',
              maxHeight: 'calc(100% - 35px)',
              boxSizing: 'border-box',
              paddingBottom: 0,
            }}
          >
            <div className={classes.contentContainer}>
              <div className={classes.formCard}>
                <div className={classes.formContent}>
                  <div className={classes.passwordSection}>
                    <div className={classes.sectionHeader}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        Create New Password
                      </Typography>
                      <Typography variant="body2" className={classes.sectionDescription}>
                        Make sure your new password is strong and unique
                      </Typography>
                    </div>

                    <div className={classes.warningBox}>
                      <div className={classes.warningIcon}>⚠️</div>
                      <div className={classes.warningText}>
                        <strong>Important Security Information:</strong>
                        <ul>
                          <li>Use at least 8 characters</li>
                          <li>Include uppercase and lowercase letters</li>
                          <li>Add numbers and special characters</li>
                          <li>Don't use personal information</li>
                          <li>Don't reuse previous passwords</li>
                        </ul>
                      </div>
                    </div>

                    <Form>
                      <div className={classes.passwordFieldsContainer}>
                        <FormikTextField
                          type="password"
                          name="confirmPassword"
                          label="New Password"
                          backendError={backendErrors.confirmPassword}
                          autoComplete="new-password"
                          required
                          onBlur={() => setHasBlurredPassword(true)}
                        />
                        <FormikTextField
                          type="password"
                          name="password"
                          label="Confirm New Password"
                          backendError={backendErrors.password}
                          autoComplete="new-password"
                          required
                        />
                        {showPasswordHint && (
                          <div className={classes.passwordHintBox}>
                            <div className={classes.hintIcon}>💡</div>
                            <div className={classes.hintText}>
                              <strong>Password Strength Recommendation:</strong>
                              <div>Your password is missing:</div>
                              <ul>
                                {passwordIssues.map((issue, index) => (
                                  <li key={index}>{issue}</li>
                                ))}
                              </ul>
                              <div className="continue-text">
                                You can continue with this password, but we recommend strengthening
                                it for better security.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
              <div className={classes.actionsContainer}>
                <BackButton className={classes.backButton} />
                <RoundedButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    !dirty ||
                    !values.confirmPassword ||
                    !values.password
                  }
                  isLoading={loading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  onClick={handleSubmit}
                  className={classes.actionButton}
                >
                  {btn.updatePassword}
                </RoundedButton>
              </div>
            </div>
          </ContentWrapper>
        );
      }}
    </Formik>
  );
};

export default PasswordChange;
